import { useMemo, useState, type ReactNode } from 'react';
import { DataError, useList } from '@hana/data';
import { findPort } from '@hana/domain';
import { useT } from '@hana/i18n';
import { HeaderTool, Page, PageHeader } from '@hana/shell';
import { Button, Chip, Input, Segmented, StateView, Table, useToast, type Column, type ScreenState } from '@hana/ui';
import { IconChevronDown, IconChevronRight, IconDots, IconDownload, IconPlus, IconSearch, IconStar, IconStarFilled, IconTrash, IconBell } from '@tabler/icons-react';
import { useP2PAccess } from './access';
import { ImpTab } from './ImpTab';
import { LaneDetail } from './LaneDetail';
import { ServiceDetail } from './ServiceDetail';
import { LtsTab } from './LtsTab';
import { COST_CARRIERS, COST_SERVE } from './costFixtures';
import { REGION_GROUPS, buildGrid, type CellStatus, type RegionGroup } from './costRates';
import { BOARD_TODAY } from './fixtures';
import { groupBy, laneRows, matchLane, type LaneRow } from './services';
import { BOARD_LTS, BOARD_SERVICES } from './servicesBoardData';
import { QUICK_TABS, SCHEDULE_STATUS, SERVICE_GROUPS, VN_PORT_OPTIONS, passService, rotationDots, serviceKpis, serviceRow, type QuickTab, type ScheduleStatus, type ServiceFilter, type ServiceGroupKey, type ServiceRow } from './svcmap';
import type { CostRate, Service } from './types';
import './ServicesMapScreen.i18n';
import s from './servicesmap.module.css';

// TODO: "hôm nay" thật khi đổi sang dữ liệu thật.
const TODAY = BOARD_TODAY;
type View = 'svc' | 'lane' | 'lts' | 'imp';
const STATUS_TX: Record<CellStatus, string> = { ok: 'ms.stOk', soon: 'ms.stSoon', exp: 'ms.stExp', none: 'ms.stNone' };

function Group({ label, n, open, onToggle, children }: { label: string; n: number; open: boolean; onToggle: () => void; children: ReactNode }) {
  return (
    <section className={s.group}>
      <button type="button" className={s.groupHead} aria-expanded={open} onClick={onToggle}>
        {open ? <IconChevronDown size={16} aria-hidden="true" /> : <IconChevronRight size={16} aria-hidden="true" />}
        <b>{label}</b>
        <span>{n}</span>
      </button>
      {open && children}
    </section>
  );
}

const SERVICE_ROWS: readonly ServiceRow[] = BOARD_SERVICES.map((v) => serviceRow(v, BOARD_LTS[v.code] ?? []));
const GROUP_LABEL = (k: ServiceGroupKey) => SERVICE_GROUPS.find((g) => g.key === k)?.label ?? k;

function ServiceTab({ dens }: { dens: 'std' | 'gon' }) {
  const t = useT();
  const toast = useToast();
  const [tab, setTab] = useState<QuickTab>('all');
  const [f, setF] = useState<ServiceFilter>({});
  const [pop, setPop] = useState<'vn' | 'car' | null>(null);
  const [sel, setSel] = useState<string | null>(null);
  const [closed, setClosed] = useState<ReadonlySet<string>>(new Set());
  const pending = () => toast({ msg: t('ms.noWrite'), tone: 'err' });

  const rows = SERVICE_ROWS;
  const quick = QUICK_TABS.find((x) => x.key === tab) ?? QUICK_TABS[0]!;
  const base = rows.filter((v) => passService(v, f));
  const vis = base.filter(quick.test);
  const allCars = [...new Set(rows.flatMap((v) => v.cars))].sort();
  const hasFlt = !!(f.vn || f.car || f.grp || f.q);
  const kpis = serviceKpis(rows);
  const kpiGo: Record<string, () => void> = {
    run: () => { setTab('all'); setF({}); },
    trips: pending,
    blank: () => setTab('blank'),
    due: () => setTab('due'),
  };
  const dd = [
    { key: 'vn' as const, label: t('ms.vnPort'), opts: VN_PORT_OPTIONS as readonly (readonly [string, string])[] },
    { key: 'car' as const, label: t('ms.carrier'), opts: allCars.map((c) => [c, c] as const) },
  ].map((d) => {
    const cur = f[d.key] ?? '';
    const pool = rows.filter((v) => passService(v, f, d.key));
    const nOf = (val: string) => pool.filter((v) => (d.key === 'vn' ? v.rot.includes(val) : v.cars.includes(val))).length;
    return { ...d, cur, curLabel: cur ? ((d.opts.find((o) => o[0] === cur) ?? ['', cur])[1]).split(' · ')[0] : '', nOf, all: pool.length };
  });
  const grpPool = rows.filter((v) => passService(v, f, 'grp') && quick.test(v));
  const chips: { key: ServiceGroupKey | ''; label: string; n: number }[] = [{ key: '', label: t('ms.all'), n: grpPool.length }, ...SERVICE_GROUPS.map((g) => ({ key: g.key, label: g.short, n: grpPool.filter((v) => v.grp === g.key).length }))];
  const toggle = (k: string) => setClosed((c) => { const n = new Set(c); if (n.has(k)) n.delete(k); else n.add(k); return n; });

  const detail = sel ? rows.find((v) => v.code === sel) : undefined;
  if (detail) return <ServiceDetail v={detail} onBack={() => setSel(null)} />;

  return (
    <>
      <div className={s.kpis}>
        {kpis.map((k) => (
          <button key={k.key} type="button" className={s.kpi} title={k.sub} aria-label={`${k.label} ${k.value}`} onClick={kpiGo[k.key]}>
            <span className={s.kpiK}>{k.label}</span>
            <b className={k.key === 'due' && k.value ? s.kpiErr : k.key === 'blank' && k.value ? s.kpiWarn : undefined}>{k.value}</b>
          </button>
        ))}
      </div>
      <section className={s.panel}>
        <div className={s.pbar}>
          <div className={s.seg} role="tablist" aria-label={t('ms.quick')}>
            {QUICK_TABS.map((x) => (
              <button key={x.key} type="button" role="tab" aria-selected={tab === x.key} title={x.tip} className={s.segBtn} onClick={() => setTab(x.key)}>
                {x.label}
                <span>{base.filter(x.test).length}</span>
              </button>
            ))}
          </div>
          <label className={s.sq}>
            <IconSearch size={16} aria-hidden="true" />
            <input value={f.q ?? ''} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder={t('ms.search')} title={t('ms.searchTip')} aria-label={t('ms.search')} />
          </label>
          {dd.map((d) => (
            <div key={d.key} className={s.ddWrap}>
              <button type="button" className={`${s.dd} ${d.cur ? s.ddOn : ''}`} aria-haspopup="listbox" aria-expanded={pop === d.key} onClick={() => setPop(pop === d.key ? null : d.key)}>
                {d.cur ? `${d.label}: ${d.curLabel}` : d.label}
                <IconChevronDown size={14} aria-hidden="true" />
              </button>
              {pop === d.key && (
                <div className={s.list} role="listbox" aria-label={d.label}>
                  {[['', t('ms.all')] as const, ...d.opts].map(([val, label]) => (
                    <button key={val || 'all'} type="button" role="option" aria-selected={d.cur === val} className={s.opt} onClick={() => { setF({ ...f, [d.key]: val }); setPop(null); }}>
                      <span>{label}</span>
                      <span>{val ? d.nOf(val) : d.all}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
          {hasFlt && (
            <button type="button" className={s.clear} onClick={() => { setF({}); setTab('all'); }}>
              <IconTrash size={16} aria-hidden="true" />
              {t('svm.clear')}
            </button>
          )}
          <span className={s.grow} />
          <span className={s.match} title={t('ms.matchN', { a: vis.length, b: rows.length })}>{vis.length}/{rows.length}</span>
          <button type="button" className={s.iconBtn} aria-label={t('ms.export')} title={t('ms.exportTip')} onClick={pending}>
            <IconDownload size={16} aria-hidden="true" />
          </button>
        </div>
        {vis.length === 0 ? (
          <div className={s.none}>
            <span>{t('ms.noMatch')}</span>
            {hasFlt && (
              <Button onClick={() => { setF({}); setTab('all'); }} icon={<IconTrash size={16} aria-hidden="true" />}>{t('svm.clear')}</Button>
            )}
          </div>
        ) : (
          <>
            <div className={s.grpTabs} role="tablist" aria-label={t('ms.group')}>
              {chips.map((c) => (
                <button key={c.key || 'all'} type="button" role="tab" aria-selected={(f.grp ?? '') === c.key} className={s.gchip} onClick={() => setF({ ...f, grp: c.key })}>
                  {c.label}
                  <span>{c.n}</span>
                </button>
              ))}
            </div>
            <div className={s.head} aria-hidden="true">
              <span className={s.cDot} />
              <span className={s.cCode} title={t('ms.hCodeTip')}>Service</span>
              <span className={s.cCar} title={t('ms.hCarTip')}>{t('ms.carrier')}</span>
              <span className={s.cRot} title={t('ms.hRotTip')}>Rotation</span>
              <span className={s.cNum}><span title={t('ms.hLoopTip')}>{t('ms.hDays')}</span><span title={t('ms.hShipsTip')}>{t('ms.hShips')}</span><span title={t('ms.hWeekTip')}>{t('ms.hWeek')}</span></span>
            </div>
            {SERVICE_GROUPS.map((g) => {
              const rs = vis.filter((v) => v.grp === g.key);
              if (!rs.length) return null;
              const open = !closed.has(g.key);
              const nDue = rs.filter((v) => v.status === 'due' || v.status === 'end').length;
              return (
                <div key={g.key}>
                  <button type="button" className={s.gHead} aria-expanded={open} onClick={() => toggle(g.key)}>
                    {open ? <IconChevronDown size={14} aria-hidden="true" /> : <IconChevronRight size={14} aria-hidden="true" />}
                    <b title={t('ms.grpTip', { a: rs.length, b: new Set(rs.flatMap((v) => v.cars)).size })}>{GROUP_LABEL(g.key)}</b>
                    <span>{rs.length}</span>
                    <span className={s.grow} />
                    {nDue > 0 && (
                      <span className={s.due} title={t('ms.dueTip', { n: nDue })}>
                        <i aria-hidden="true" />
                        {nDue}
                      </span>
                    )}
                  </button>
                  {open && rs.map((v) => {
                    const tag = !v.active ? t('ms.stopped') : '';
                    const dots = rotationDots(v);
                    const rot = v.rot.filter((p, i, a) => p !== a[i - 1]);
                    return (
                      <div
                        key={v.code}
                        role="button"
                        tabIndex={0}
                        className={`${s.row} ${dens === 'gon' ? s.gon : ''} ${v.active ? '' : s.off}`}
                        aria-label={`${t('ms.open', { code: v.code })} · ${v.name}${tag ? ` · ${tag}` : ''}${v.locked ? ` · ${t('ms.locked')}` : ''}`}
                        onClick={() => setSel(v.code)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSel(v.code); } }}
                      >
                        <span className={`${s.rdot} ${s[`l_${v.status}`]}`} role="img" aria-label={SCHEDULE_STATUS[v.status]} title={`${SCHEDULE_STATUS[v.status]}${v.active ? ` · ${t('ms.schedTo', { v: v.last })}` : ` · ${t('ms.noNew')}`}`} />
                        <b className={s.cCode} title={`${v.code} · ${v.name}${tag ? ` · ${tag}` : ''} · T/T ${v.vn} → ${v.main} ${t('ms.days', { n: v.tt })} · ${v.locked ? t('ms.locked') : t('ms.unlocked')}`}>{v.code}</b>
                        <div className={`${s.cCar} ${s.carChips}`} aria-label={t('ms.carsOf', { v: v.cars.join(', ') })}>
                          {v.cars.slice(0, 4).map((c, i) => (
                            <span key={c} className={i === 0 ? s.opr : s.slot} title={`${c} · ${i === 0 ? t('ms.operator') : t('ms.slot')}`}>{c}</span>
                          ))}
                          {v.cars.length > 4 && <span className={s.slot} title={v.cars.slice(4).join(' · ')}>+{v.cars.length - 4}</span>}
                        </div>
                        <div className={`${s.cRot} ${s.rotWrap}`} role="img" aria-label={`${t('ms.rotN', { n: rot.length })}: ${rot.join(' → ')} · T/T ${v.vn} → ${v.main} ${t('ms.days', { n: v.tt })}`}>
                          {dots.map((d) => (
                            <span key={`${d.port}-${d.title}`} className={s.rotItem}>
                              <span className={`${s.pt} ${d.vn ? s.ptVn : ''}`} title={d.title}>
                                {d.label && <span className={`${s.ptLab} ${d.above ? s.ptUp : ''} ${d.vn ? s.ptLabVn : ''}`}>{d.port}</span>}
                              </span>
                              {d.line && <span className={s.ln} />}
                            </span>
                          ))}
                        </div>
                        <div className={s.cNum}>
                          <span role="img" aria-label={t('ms.loopN', { n: v.circ })} title={t('ms.loopN', { n: v.circ })}>{v.circ}</span>
                          <span role="img" aria-label={t('ms.shipsN', { n: v.ships })} title={t('ms.shipsN', { n: v.ships })}>{v.ships}</span>
                          <span role="img" aria-label={t('ms.perWeek')} title={t('ms.perWeek')}>1</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
            <div className={s.legend}>
              {(Object.keys(SCHEDULE_STATUS) as ScheduleStatus[]).map((k) => (
                <span key={k}><i className={`${s.rdot} ${s[`l_${k}`]}`} />{SCHEDULE_STATUS[k]}</span>
              ))}
              <span><i className={`${s.pt} ${s.ptVn}`} />{t('ms.vnPort')}</span>
            </div>
          </>
        )}
      </section>
    </>
  );
}

export function ServicesMapScreen() {
  const t = useT();
  const access = useP2PAccess();
  const rates = useList('costRate');
  const svcs = useList('service');
  const toast = useToast();
  const [view, setView] = useState<View>('svc');
  const [dens, setDens] = useState<'std' | 'gon'>('std');
  const [q, setQ] = useState('');
  const [group, setGroup] = useState<RegionGroup | null>(null);
  const [closed, setClosed] = useState<ReadonlySet<string>>(new Set());
  const [follow, setFollow] = useState<ReadonlyMap<string, boolean>>(new Map());
  const [laneSel, setLaneSel] = useState<string | null>(null);
  const [pics, setPics] = useState<ReadonlyMap<string, string>>(new Map());

  const allRates = (rates.data?.items ?? []) as CostRate[];
  const allSvcs = (svcs.data?.items ?? []) as Service[];
  const lanes = useMemo(() => laneRows(buildGrid(allRates, COST_SERVE, COST_CARRIERS, TODAY), allRates, allSvcs), [allRates, allSvcs]);
  const laneDetail = laneSel ? lanes.find((l) => l.key === laneSel) : undefined;
  const laneShown = lanes.filter((l) => (!group || l.group === group) && matchLane(l, q));
  const isFollow = (l: LaneRow) => follow.get(l.key) ?? l.follow;
  const toggle = (k: string) => setClosed((c) => { const n = new Set(c); if (n.has(k)) n.delete(k); else n.add(k); return n; });

  const failed = rates.error ?? svcs.error;
  let state: ScreenState = 'ready';
  if (access.force) state = access.force;
  else if (rates.isPending || svcs.isPending) state = 'loading';
  else if (failed) state = failed instanceof DataError && failed.code === 'noperm' ? 'noperm' : 'error';
  else if (!allSvcs.length && !lanes.length) state = 'empty';

  const laneCols: Column<LaneRow>[] = [
    { key: 'lane', header: t('ms.lane'), width: 150, cell: (r) => <b title={`${findPort(r.pol)?.name ?? r.pol} → ${findPort(r.pod)?.name ?? r.pod}`}>{r.label}</b> },
    {
      key: 'live',
      header: t('ms.carrier'),
      width: 70,
      cell: (r) => (
        <span title={t('ms.liveTip', { a: r.live, b: r.total })} className={r.live * 2 < r.total ? s.weak : undefined}>
          {r.live}/{r.total}
        </span>
      ),
    },
    { key: 'tt', header: 'T/T', width: 70, minor: true, cell: (r) => (r.tt === null ? '–' : `${r.viaOnly ? '~' : ''}${r.tt}`) },
    { key: 'freq', header: t('ms.hWeekCol'), width: 80, minor: true, cell: (r) => r.freq || '–' },
    {
      key: 'status',
      header: t('ms.price'),
      width: 56,
      cell: (r) => <span className={`${s.sdot} ${s[`s_${r.status}`]}`} role="img" aria-label={t(STATUS_TX[r.status])} title={t(STATUS_TX[r.status])} />,
    },
    { key: 'pend', header: t('ms.ask'), width: 64, minor: true, cell: (r) => (r.pending ? <Chip tone="warn" label={t('ms.askPending', { n: r.pending })}>{r.pending}</Chip> : null) },
    { key: 'pic', header: 'PIC', width: 64, minor: true, cell: (r) => <span className={s.pic} title={t('ms.picTip', { v: r.pic })}>{r.pic}</span> },
    {
      key: 'follow',
      header: <IconStar size={15} aria-label="Follow" />,
      width: 56,
      cell: (r) => (
        <button
          type="button"
          className={s.star}
          aria-pressed={isFollow(r)}
          aria-label={isFollow(r) ? t('ms.unfollow', { v: r.label }) : t('ms.follow', { v: r.label })}
          title={isFollow(r) ? t('ms.followingTip') : t('ms.followTip')}
          onClick={(e) => { e.stopPropagation(); setFollow(new Map(follow).set(r.key, !isFollow(r))); }}
        >
          {isFollow(r) ? <IconStarFilled size={17} aria-hidden="true" /> : <IconStar size={17} aria-hidden="true" />}
        </button>
      ),
    },
  ];

  const pending = () => toast({ msg: t('ms.noWrite'), tone: 'err' });
  const body =
    view === 'svc' ? (
      <ServiceTab dens={dens} />
    ) : view === 'lane' && laneDetail ? (
      <LaneDetail
        lane={laneDetail}
        services={allSvcs}
        rates={allRates}
        served={[...(COST_SERVE.get(laneDetail.key) ?? [])]}
        follow={isFollow(laneDetail)}
        onFollow={() => setFollow(new Map(follow).set(laneDetail.key, !isFollow(laneDetail)))}
        pic={pics.get(laneDetail.key) ?? laneDetail.pic}
        onPic={(p) => setPics(new Map(pics).set(laneDetail.key, p))}
        onBack={() => setLaneSel(null)}
      />
    ) : view === 'lane' ? (
      <>
        <div className={s.bar}>
          <div className={s.search}>
            <Input aria-label={t('ms.searchLane')} placeholder={t('ms.searchLane')} icon={<IconSearch size={16} aria-hidden="true" />} value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <div className={s.chipsRow} role="group" aria-label={t('ms.group')}>
            <button type="button" className={s.chipBtn} aria-pressed={group === null} onClick={() => setGroup(null)}>{t('ms.all')}</button>
            {REGION_GROUPS.map((g) => (
              <button key={g.key} type="button" className={s.chipBtn} aria-pressed={group === g.key} onClick={() => setGroup(group === g.key ? null : g.key)}>{g.label}</button>
            ))}
          </div>
          <span className={s.count}>{t('ms.lanesCount', { a: laneShown.length, b: lanes.length })}</span>
        </div>
        {laneShown.length === 0 ? (
          <StateView state="empty" title={t('ms.noLane')} desc={t('ms.noLaneD')} action={<Button variant="secondary" onClick={() => { setQ(''); setGroup(null); }}>{t('svm.clear')}</Button>} />
        ) : (
          groupBy(laneShown, (l) => l.group).map((g) => (
            <Group key={g.key} label={g.label} n={g.rows.length} open={!closed.has(`l${g.key}`)} onToggle={() => toggle(`l${g.key}`)}>
              <Table label={t('ms.laneOf', { v: g.label })} columns={laneCols} rows={g.rows} rowKey={(r) => r.key} onRowClick={(r) => setLaneSel(r.key)} dense />
            </Group>
          ))
        )}
      </>
    ) : view === 'lts' ? (
      <LtsTab rows={SERVICE_ROWS} dens={dens} onView={setView} />
    ) : (
      <ImpTab rows={SERVICE_ROWS} onView={setView} />
    );

  return (
    <>
      <Page kind="table">
      <PageHeader
        title="Services Map"
        tabs={
          <Segmented
            label={t('ms.view')}
            value={view}
            onChange={setView}
            items={[{ key: 'svc', label: 'Service', n: SERVICE_ROWS.length }, { key: 'lane', label: t('ms.lane'), n: lanes.length }, { key: 'lts', label: t('ms.lts'), n: SERVICE_ROWS.reduce((n, v) => n + (v.active ? v.blank : 0), 0) + SERVICE_ROWS.filter((v) => v.status === 'due' || v.status === 'end').length }, { key: 'imp', label: t('ms.imp'), n: 5 }]}
          />
        }
        actions={
          <>
            <Segmented label={t('ms.density')} kind="toggle" value={dens} onChange={setDens} items={[{ key: 'std', label: t('ms.std') }, { key: 'gon', label: t('ms.compact') }]} />
            <HeaderTool label={t('ms.noti')} icon={IconBell} n={3} />
            <HeaderTool label={t('svm.more')} icon={IconDots} />
            <Button variant="primary" icon={view === 'imp' ? undefined : <IconPlus size={16} aria-hidden="true" />} onClick={pending}>
              {view === 'lane' ? t('ms.addLane') : view === 'imp' ? t('ms.pickFile') : t('ms.addSvc')}
            </Button>
          </>
        }
      />
      <div className={s.page}>
        <StateView
          state={state}
          title={state === 'noperm' ? t('ms.nopermT') : state === 'error' ? t('ms.errT') : state === 'empty' ? t('ms.emptyT') : undefined}
          desc={state === 'noperm' ? t('ms.nopermD') : state === 'error' ? t('ms.errD') : undefined}
          action={state === 'error' ? <Button onClick={() => { void rates.refetch(); void svcs.refetch(); }}>{t('svm.retry')}</Button> : undefined}
        >
          {body}
        </StateView>
      </div>
      </Page>
    </>
  );
}
